import { useMutation } from "@tanstack/react-query"
import { Bell, KeyRound, Users } from "lucide-react"
import { useId, useState } from "react"
import { useSearchParams } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { GruposTelegram } from "@/componentes/avisos/GruposTelegram"
import { Telegram } from "@/componentes/avisos/Telegram"
import { type Pestana, Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { LARGO_MINIMO_CLAVE } from "@/lib/contrasenas"
import { usarSesion } from "@/lib/sesion"

/**
 * Mi cuenta: la contraseña propia y por dónde me llegan los avisos.
 *
 * ⚠ **Cambiar la propia exige la actual.** No es burocracia: sin ese requisito, una
 * sesión olvidada en un equipo compartido o un XSS de un minuto se convierten en
 * apropiación de la cuenta — el atacante fija una clave nueva y entra cuando quiera.
 * Pedirla convierte «tener la sesión» en algo que caduca.
 *
 * ⚠ **Cambiarla cierra las demás sesiones, pero no ésta.** El servidor reemite la
 * cookie en la misma respuesta; los otros dispositivos se quedan fuera, que es
 * justo lo que se busca al cambiarla porque se sospecha de alguien.
 *
 * ⚠ **Sin bloqueo de pegado.** Romper los gestores de contraseñas empeora la
 * seguridad real, y WCAG 3.3.8 lo prohíbe explícitamente.
 *
 * ⚠ **En pestañas, con la elegida en la URL** (`?vista=`, 23/10): contraseña, avisos y
 * grupos iban en una sola columna, y con cada grupo creado la página crecía sin fin.
 */

type Vista = "seguridad" | "avisos" | "grupos"

const MINIMO = LARGO_MINIMO_CLAVE

export function Perfil() {
  const usuario = usarSesion((e) => e.usuario)
  const [params, setParams] = useSearchParams()
  const conGrupos = tienePermiso(usuario, "usuario:administrar")
  const pestanas: Pestana<Vista>[] = [
    { valor: "seguridad", etiqueta: "Seguridad", icono: KeyRound },
    { valor: "avisos", etiqueta: "Avisos", icono: Bell },
    // Los grupos los reparte quien reparte accesos: lo que ve un grupo entero es una
    // decisión de acceso. El servidor lo exige igual; esto sólo esconde.
    ...(conGrupos
      ? [{ valor: "grupos" as const, etiqueta: "Grupos de Telegram", icono: Users }]
      : []),
  ]
  const pedida = params.get("vista")
  const vista: Vista = pestanas.some((p) => p.valor === pedida) ? (pedida as Vista) : "seguridad"

  return (
    <div className={`mx-auto ${vista === "grupos" ? "max-w-5xl" : "max-w-2xl"}`}>
      <h1 className="text-xl font-semibold">Mi cuenta</h1>
      <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
        {usuario?.nombre} · {usuario?.email}
      </p>

      <div className="mt-4">
        <Pestanas
          pestanas={pestanas}
          activa={vista}
          onCambiar={(v) => setParams(v === "seguridad" ? {} : { vista: v }, { replace: true })}
          etiqueta="Ajustes de mi cuenta"
          idPanel="panel-cuenta"
          prefijoId="pestana-cuenta"
        />
      </div>

      <div
        id="panel-cuenta"
        role="tabpanel"
        aria-labelledby={`pestana-cuenta-${vista}`}
        className="mt-4"
      >
        {vista === "seguridad" && <Contrasena />}
        {/* El enlace con Telegram vive aquí y no en Avisos: es un ajuste de la cuenta
            de uno, igual que la contraseña, y no una bandeja que se lee. */}
        {vista === "avisos" && <Telegram />}
        {vista === "grupos" && <GruposTelegram />}
      </div>
    </div>
  )
}

function Contrasena() {
  const idBase = useId()
  const [actual, setActual] = useState("")
  const [nueva, setNueva] = useState("")
  const [repetida, setRepetida] = useState("")

  const cambiar = useMutation({
    mutationFn: () => api.post("/api/v1/perfil/password", { actual, nueva }),
    onSuccess: () => {
      setActual("")
      setNueva("")
      setRepetida("")
    },
  })

  // La repetición se comprueba aquí y no en el servidor: es un error de tecleo, no
  // una regla de negocio, y mandarlo a la red para que vuelva es un viaje de más.
  const noCoincide = repetida !== "" && nueva !== repetida
  const listo = actual !== "" && nueva.length >= MINIMO && nueva === repetida

  return (
    <section className="tarjeta">
      <h2 className="flex items-center gap-2 text-sm font-medium">
        <KeyRound size={14} aria-hidden={true} />
        Contraseña
      </h2>

      <form
        aria-label="Cambiar contraseña"
        className="mt-3 space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (listo) cambiar.mutate()
        }}
      >
        <label className="block text-sm" htmlFor={`${idBase}-actual`}>
          Contraseña actual
          <input
            id={`${idBase}-actual`}
            type="password"
            autoComplete="current-password"
            className="campo mt-1"
            value={actual}
            onChange={(e) => setActual(e.target.value)}
          />
        </label>

        <div>
          <label className="block text-sm" htmlFor={`${idBase}-nueva`}>
            Contraseña nueva
            <input
              id={`${idBase}-nueva`}
              type="password"
              autoComplete="new-password"
              aria-describedby={`${idBase}-pista`}
              className="campo mt-1"
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
            />
          </label>
          {/* La pista fuera del `<label>`: dentro pasa a formar parte del nombre
                accesible y un lector la anuncia entera en cada entrada al campo. */}
          <p id={`${idBase}-pista`} className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            Mínimo {MINIMO} caracteres. Larga vale más que complicada: no se piden mayúsculas ni
            símbolos porque empujan a `Clave1!` y a apuntarla en un papel.
          </p>
        </div>

        <label className="block text-sm" htmlFor={`${idBase}-repetida`}>
          Repite la nueva
          <input
            id={`${idBase}-repetida`}
            type="password"
            autoComplete="new-password"
            aria-invalid={noCoincide}
            className="campo mt-1"
            value={repetida}
            onChange={(e) => setRepetida(e.target.value)}
          />
        </label>
        {noCoincide && (
          <p className="text-xs" style={{ color: "var(--destructive)" }}>
            Las dos no coinciden.
          </p>
        )}

        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Al cambiarla se cierran tus sesiones en los demás dispositivos. Ésta sigue abierta.
        </p>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!listo || cambiar.isPending}
            className="boton-primario px-3 py-1.5 text-sm disabled:opacity-50"
          >
            {cambiar.isPending ? "Guardando…" : "Cambiar contraseña"}
          </button>
        </div>

        {cambiar.isSuccess && (
          <p role="status" className="text-sm" style={{ color: "var(--success)" }}>
            Contraseña cambiada.
          </p>
        )}
        {cambiar.isError && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {cambiar.error instanceof ErrorApi
              ? cambiar.error.message
              : "No se pudo cambiar la contraseña."}
          </p>
        )}
      </form>
    </section>
  )
}
